import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Shield, Zap, Camera, Bell, BarChart3, Building2,
  CheckCircle, ArrowLeft, Download, Printer, Star, Clock,
  Users, Lock, Cloud, Euro, Calendar, Phone, Mail, Globe
} from 'lucide-react';

const PropuestaComercial = () => {
  const navigate = useNavigate();
  const printRef = useRef();

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header de control (no se imprime) */}
      <div className="print:hidden bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver
          </button>
          <div className="flex items-center gap-4">
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 transition"
            >
              <Printer className="w-5 h-5" />
              Imprimir / Guardar PDF
            </button>
          </div>
        </div>
      </div>

      {/* Documento */}
      <div ref={printRef} className="max-w-4xl mx-auto bg-white shadow-xl my-8 print:my-0 print:shadow-none">

        {/* Portada */}
        <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white p-16 print:p-12">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
              <FileText className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Sistema de Multas</h1>
              <p className="text-blue-200 text-sm">Grupo Ibérica</p>
            </div>
          </div>

          <h2 className="text-5xl font-bold mb-6 leading-tight">
            Propuesta Comercial
          </h2>
          <h3 className="text-2xl text-blue-100 mb-8">
            Sistema de Gestión Inteligente de Multas de Tráfico
          </h3>

          <div className="grid grid-cols-3 gap-8 mt-16">
            <div className="text-center">
              <p className="text-4xl font-bold">70%</p>
              <p className="text-blue-200 text-sm">Automatización</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-bold">&lt;2min</p>
              <p className="text-blue-200 text-sm">Tiempo proceso</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-bold">99.9%</p>
              <p className="text-blue-200 text-sm">Disponibilidad</p>
            </div>
          </div>

          <div className="mt-16 pt-8 border-t border-white/20">
            <p className="text-sm text-blue-200">
              Documento confidencial | Válido hasta: {new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('es-ES')}
            </p>
          </div>
        </div>

        {/* Índice */}
        <div className="p-12 border-b page-break-after">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Índice</h2>
          <div className="space-y-4">
            {[
              { num: '01', title: 'Resumen Ejecutivo', page: 3 },
              { num: '02', title: 'Problema y Solución', page: 4 },
              { num: '03', title: 'Funcionalidades', page: 5 },
              { num: '04', title: 'Arquitectura Técnica', page: 7 },
              { num: '05', title: 'Planes y Precios', page: 8 },
              { num: '06', title: 'ROI y Beneficios', page: 10 },
              { num: '07', title: 'Implementación', page: 11 },
              { num: '08', title: 'Sobre Nosotros', page: 12 },
            ].map((item, index) => (
              <div key={index} className="flex items-center justify-between py-3 border-b border-gray-100">
                <div className="flex items-center gap-4">
                  <span className="text-blue-600 font-mono font-bold">{item.num}</span>
                  <span className="text-gray-900">{item.title}</span>
                </div>
                <span className="text-gray-400">{item.page}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Sección 1: Resumen Ejecutivo */}
        <div className="p-12 border-b">
          <div className="flex items-center gap-3 mb-6">
            <span className="text-blue-600 font-mono font-bold text-lg">01</span>
            <h2 className="text-2xl font-bold text-gray-900">Resumen Ejecutivo</h2>
          </div>

          <div className="bg-blue-50 rounded-xl p-6 mb-8">
            <p className="text-lg text-gray-700 leading-relaxed">
              <strong>Sistema de Multas</strong> es una plataforma SaaS que automatiza completamente el ciclo de gestión
              de multas de tráfico para flotas empresariales: desde la recepción de la notificación hasta
              la comunicación del conductor al organismo sancionador.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="bg-gray-50 rounded-xl p-6">
              <h4 className="font-bold text-gray-900 mb-3">El Problema</h4>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  Proceso manual lento y propenso a errores
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  Riesgo de superar el plazo legal de 20 días
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  Dependencia de intermediarios
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  Falta de visibilidad y control
                </li>
              </ul>
            </div>
            <div className="bg-green-50 rounded-xl p-6">
              <h4 className="font-bold text-gray-900 mb-3">La Solución</h4>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  Automatización del 70% de identificaciones
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  Alertas proactivas por nivel de urgencia
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  Conexión directa con DGT
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  Dashboard en tiempo real
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Sección 2: Funcionalidades */}
        <div className="p-12 border-b page-break-before">
          <div className="flex items-center gap-3 mb-6">
            <span className="text-blue-600 font-mono font-bold text-lg">03</span>
            <h2 className="text-2xl font-bold text-gray-900">Funcionalidades Principales</h2>
          </div>

          <div className="grid grid-cols-2 gap-6">
            {[
              {
                icon: Zap,
                title: 'Identificación Automática',
                description: 'Cuando mantienes actualizadas las asignaciones conductor-vehículo, el sistema identifica automáticamente quién conducía.',
                color: 'yellow'
              },
              {
                icon: Camera,
                title: 'OCR Inteligente',
                description: 'Sube foto o PDF de cualquier multa física. Nuestra IA extrae automáticamente todos los datos relevantes.',
                color: 'purple'
              },
              {
                icon: Shield,
                title: 'Conexión DGT Directa',
                description: 'Consulta y sincroniza multas directamente desde la sede electrónica de la DGT con certificado digital.',
                color: 'blue'
              },
              {
                icon: Bell,
                title: 'Alertas por Urgencia',
                description: 'Sistema de alertas con 4 niveles de urgencia. Emails diarios automáticos y dashboard visual.',
                color: 'red'
              },
              {
                icon: BarChart3,
                title: 'Reportes y Analytics',
                description: 'Dashboard en tiempo real, exportación a Excel con filtros avanzados, reportes automáticos.',
                color: 'green'
              },
              {
                icon: Building2,
                title: 'Multi-Empresa',
                description: 'Gestiona múltiples empresas desde una única plataforma. Ideal para gestorías y grupos empresariales.',
                color: 'indigo'
              }
            ].map((feature, index) => (
              <div key={index} className="border rounded-xl p-6">
                <div className={`w-12 h-12 bg-${feature.color}-100 rounded-xl flex items-center justify-center mb-4`}>
                  <feature.icon className={`w-6 h-6 text-${feature.color}-600`} />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Sección: Fuentes de Datos */}
        <div className="p-12 border-b">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Fuentes de Datos Integradas</h3>

          <div className="overflow-hidden rounded-xl border">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-6 py-4 font-semibold text-gray-900">Fuente</th>
                  <th className="text-left px-6 py-4 font-semibold text-gray-900">Tipo</th>
                  <th className="text-left px-6 py-4 font-semibold text-gray-900">Descripción</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                <tr>
                  <td className="px-6 py-4 font-medium">GoodOk</td>
                  <td className="px-6 py-4"><span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs">Webhook</span></td>
                  <td className="px-6 py-4 text-sm text-gray-600">Notificaciones automáticas en tiempo real</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium">DGT Directo</td>
                  <td className="px-6 py-4"><span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs">Scraping</span></td>
                  <td className="px-6 py-4 text-sm text-gray-600">Consulta con certificado digital de empresa</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium">OCR (Textract)</td>
                  <td className="px-6 py-4"><span className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs">Upload</span></td>
                  <td className="px-6 py-4 text-sm text-gray-600">Sube foto/PDF de multa física</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Sección: Arquitectura */}
        <div className="p-12 border-b page-break-before">
          <div className="flex items-center gap-3 mb-6">
            <span className="text-blue-600 font-mono font-bold text-lg">04</span>
            <h2 className="text-2xl font-bold text-gray-900">Arquitectura Técnica</h2>
          </div>

          <div className="bg-slate-900 rounded-xl p-8 text-white font-mono text-sm mb-8">
            <pre className="whitespace-pre-wrap">
{`┌─────────────────────────────────────────────────────────────┐
│                    MULTAFLOW ARCHITECTURE                    │
├─────────────────────────────────────────────────────────────┤
│                                                              │
│  ┌──────────┐  ┌──────────┐  ┌──────────┐                   │
│  │  GoodOk  │  │   DGT    │  │   OCR    │   DATA SOURCES    │
│  │ (webhook)│  │(scraping)│  │(Textract)│                   │
│  └────┬─────┘  └────┬─────┘  └────┬─────┘                   │
│       │             │             │                          │
│       └─────────────┼─────────────┘                          │
│                     ▼                                        │
│  ┌─────────────────────────────────────────────────────┐    │
│  │              BACKEND (Node.js + Express)             │    │
│  │  ┌──────────┐  ┌──────────┐  ┌──────────┐          │    │
│  │  │ Sources  │  │Processor │  │  Alerts  │          │    │
│  │  └──────────┘  └──────────┘  └──────────┘          │    │
│  └─────────────────────────────────────────────────────┘    │
│                     │                                        │
│       ┌─────────────┼─────────────┐                          │
│       ▼             ▼             ▼                          │
│  ┌──────────┐  ┌──────────┐  ┌──────────┐                   │
│  │PostgreSQL│  │  Redis   │  │   S3     │   AWS SERVICES    │
│  │   (RDS)  │  │ (Cache)  │  │(Storage) │                   │
│  └──────────┘  └──────────┘  └──────────┘                   │
│                                                              │
│  ┌─────────────────────────────────────────────────────┐    │
│  │              FRONTEND (React + Tailwind)             │    │
│  │              Dashboard | Alertas | OCR               │    │
│  └─────────────────────────────────────────────────────┘    │
│                                                              │
└─────────────────────────────────────────────────────────────┘`}
            </pre>
          </div>

          <div className="grid grid-cols-3 gap-6">
            <div className="text-center p-4 bg-gray-50 rounded-xl">
              <Cloud className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <h4 className="font-bold text-gray-900">100% Cloud</h4>
              <p className="text-sm text-gray-500">AWS Infrastructure</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-xl">
              <Lock className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <h4 className="font-bold text-gray-900">Seguro</h4>
              <p className="text-sm text-gray-500">HTTPS + Cifrado</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-xl">
              <Globe className="w-8 h-8 text-purple-600 mx-auto mb-2" />
              <h4 className="font-bold text-gray-900">API REST</h4>
              <p className="text-sm text-gray-500">Integración fácil</p>
            </div>
          </div>
        </div>

        {/* Sección: Planes y Precios */}
        <div className="p-12 border-b page-break-before">
          <div className="flex items-center gap-3 mb-6">
            <span className="text-blue-600 font-mono font-bold text-lg">05</span>
            <h2 className="text-2xl font-bold text-gray-900">Planes y Precios</h2>
          </div>

          <div className="grid grid-cols-3 gap-6 mb-8">
            {[
              {
                name: 'Starter',
                price: '149€',
                multas: '50',
                extra: '3,50€',
                features: ['Hasta 50 multas/mes', 'Identificación automática', 'OCR básico', 'Alertas email', '1 usuario'],
                highlighted: false
              },
              {
                name: 'Business',
                price: '399€',
                multas: '200',
                extra: '3,00€',
                features: ['Hasta 200 multas/mes', 'Todo de Starter +', 'Conexión DGT', 'Reportes Excel', '5 usuarios', 'Soporte prioritario'],
                highlighted: true
              },
              {
                name: 'Enterprise',
                price: '799€',
                multas: '500',
                extra: '2,50€',
                features: ['Hasta 500 multas/mes', 'Todo de Business +', 'API integración', 'Multi-empresa', 'Usuarios ilimitados', 'SLA 99.9%', 'Soporte 24/7'],
                highlighted: false
              }
            ].map((plan, index) => (
              <div
                key={index}
                className={`rounded-xl p-6 ${
                  plan.highlighted
                    ? 'bg-blue-600 text-white ring-4 ring-blue-200'
                    : 'bg-gray-50 text-gray-900'
                }`}
              >
                {plan.highlighted && (
                  <span className="text-xs bg-white text-blue-600 px-2 py-1 rounded-full font-medium">
                    Más Popular
                  </span>
                )}
                <h3 className="text-xl font-bold mt-2">{plan.name}</h3>
                <div className="mt-4">
                  <span className="text-3xl font-bold">{plan.price}</span>
                  <span className={plan.highlighted ? 'text-blue-100' : 'text-gray-500'}>/mes</span>
                </div>
                <p className={`text-sm mt-1 ${plan.highlighted ? 'text-blue-100' : 'text-gray-500'}`}>
                  Hasta {plan.multas} multas incluidas
                </p>
                <ul className="mt-6 space-y-2">
                  {plan.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start gap-2 text-sm">
                      <CheckCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${plan.highlighted ? 'text-blue-200' : 'text-green-500'}`} />
                      {feature}
                    </li>
                  ))}
                </ul>
                <p className={`text-xs mt-4 ${plan.highlighted ? 'text-blue-200' : 'text-gray-500'}`}>
                  +{plan.extra} por multa adicional
                </p>
              </div>
            ))}
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
            <h4 className="font-bold text-yellow-800 mb-2">Planes Personalizados</h4>
            <p className="text-sm text-yellow-700">
              Para más de 500 multas/mes o requisitos especiales (multi-sede, integración ERP, white-label),
              contacte con nosotros para un presupuesto personalizado.
            </p>
          </div>
        </div>

        {/* Sección: ROI */}
        <div className="p-12 border-b">
          <div className="flex items-center gap-3 mb-6">
            <span className="text-blue-600 font-mono font-bold text-lg">06</span>
            <h2 className="text-2xl font-bold text-gray-900">ROI y Beneficios</h2>
          </div>

          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-8 mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Ejemplo: Empresa con 200 multas/mes</h3>
            <div className="grid grid-cols-2 gap-8">
              <div>
                <h4 className="font-semibold text-red-600 mb-3">Sin Sistema de Multas</h4>
                <ul className="space-y-2 text-sm">
                  <li className="flex justify-between">
                    <span>Tiempo empleado</span>
                    <span className="font-mono">40h/mes</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Coste personal (25€/h)</span>
                    <span className="font-mono">1.000€</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Gestiones externas (8€/u)</span>
                    <span className="font-mono">1.600€</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Errores y sanciones</span>
                    <span className="font-mono">~500€</span>
                  </li>
                  <li className="flex justify-between font-bold border-t pt-2 mt-2">
                    <span>Total</span>
                    <span className="font-mono text-red-600">3.100€/mes</span>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-green-600 mb-3">Con Sistema de Multas</h4>
                <ul className="space-y-2 text-sm">
                  <li className="flex justify-between">
                    <span>Plan Business</span>
                    <span className="font-mono">399€</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Tiempo empleado (8h/mes)</span>
                    <span className="font-mono">200€</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Multas extra (0)</span>
                    <span className="font-mono">0€</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Errores y sanciones</span>
                    <span className="font-mono">~0€</span>
                  </li>
                  <li className="flex justify-between font-bold border-t pt-2 mt-2">
                    <span>Total</span>
                    <span className="font-mono text-green-600">599€/mes</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-green-200 text-center">
              <p className="text-2xl font-bold text-green-700">
                Ahorro: 2.501€/mes = 30.012€/año
              </p>
              <p className="text-sm text-green-600 mt-1">
                ROI en el primer mes
              </p>
            </div>
          </div>
        </div>

        {/* Sección: Implementación */}
        <div className="p-12 border-b page-break-before">
          <div className="flex items-center gap-3 mb-6">
            <span className="text-blue-600 font-mono font-bold text-lg">07</span>
            <h2 className="text-2xl font-bold text-gray-900">Plan de Implementación</h2>
          </div>

          <div className="space-y-6">
            {[
              {
                week: 'Semana 1',
                title: 'Setup y Configuración',
                tasks: ['Alta en plataforma', 'Configuración empresa', 'Carga de asignaciones', 'Formación usuarios']
              },
              {
                week: 'Semana 2',
                title: 'Integración',
                tasks: ['Configuración GoodOk (si aplica)', 'Instalación certificado DGT', 'Pruebas de conexión', 'Ajustes']
              },
              {
                week: 'Semana 3-4',
                title: 'Producción',
                tasks: ['Procesamiento real de multas', 'Monitorización', 'Optimización', 'Soporte activo']
              }
            ].map((phase, index) => (
              <div key={index} className="flex gap-6">
                <div className="w-24 flex-shrink-0">
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                    {phase.week}
                  </span>
                </div>
                <div className="flex-1 bg-gray-50 rounded-xl p-6">
                  <h4 className="font-bold text-gray-900 mb-3">{phase.title}</h4>
                  <ul className="grid grid-cols-2 gap-2">
                    {phase.tasks.map((task, tIndex) => (
                      <li key={tIndex} className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        {task}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Sección: Sobre Nosotros */}
        <div className="p-12 border-b">
          <div className="flex items-center gap-3 mb-6">
            <span className="text-blue-600 font-mono font-bold text-lg">08</span>
            <h2 className="text-2xl font-bold text-gray-900">Sobre Nosotros</h2>
          </div>

          <div className="grid grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Grupo Ibérica</h3>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Con más de 15 años de experiencia en soluciones tecnológicas para el sector transporte,
                Grupo Ibérica desarrolla Sistema de Multas como respuesta a una necesidad real de nuestros clientes:
                simplificar y automatizar la gestión de multas de tráfico.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  +500 empresas confían en nosotros
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Equipo técnico especializado
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Soporte en español 24/7
                </li>
              </ul>
            </div>
            <div className="bg-gray-50 rounded-xl p-6">
              <h4 className="font-bold text-gray-900 mb-4">Contacto Comercial</h4>
              <ul className="space-y-4">
                <li className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Mail className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium">comercial@grupoiberica.com</p>
                  </div>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Phone className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Teléfono</p>
                    <p className="font-medium">+34 900 123 456</p>
                  </div>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Globe className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Web</p>
                    <p className="font-medium">multas.grupoiberica.com</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Contraportada */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 text-white p-16 text-center">
          <div className="w-20 h-20 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-8">
            <FileText className="w-10 h-10" />
          </div>
          <h2 className="text-3xl font-bold mb-4">¿Listo para empezar?</h2>
          <p className="text-slate-300 text-lg mb-8">
            Prueba Sistema de Multas gratis durante 14 días.<br />
            Sin compromiso. Sin tarjeta de crédito.
          </p>
          <div className="inline-flex items-center gap-4 bg-white/10 rounded-full px-6 py-3">
            <span className="text-slate-300">multas.grupoiberica.com</span>
          </div>
          <div className="mt-16 pt-8 border-t border-white/10">
            <p className="text-sm text-slate-400">
              © 2026 Sistema de Multas - Grupo Ibérica. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </div>

      {/* Estilos de impresión */}
      <style jsx global>{`
        @media print {
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .page-break-before {
            page-break-before: always;
          }
          .page-break-after {
            page-break-after: always;
          }
          @page {
            margin: 0;
            size: A4;
          }
        }
      `}</style>
    </div>
  );
};

export default PropuestaComercial;
